/**
 * Screens 09 «Сообщения» and 10 «Чат и запись» (salon-mockup.html inbox /
 * chat; doc 04 §8). Owner's choices, 2026-10-01: «Записать из переписки»
 * pre-sets the client only, the Instagram chip is faded until that channel
 * exists (as D-57 fades «Каналы»), and «Язык напоминаний» stays hidden with the
 * reminders (D-66). The SMS chip is hidden: not supported for now (owner, 2026-10-04).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { AppBar, INBOX_POLL_MS, Shell, inboxKey } from '../components/Shell';
import { toast } from '../components/Toast';
import { ChannelLogo, Icon } from '../components/icons';
import { ApiError, api } from '../core/api';
import { errorText, formatAmount, formatTime } from '../i18n';
import { serviceName } from '../lib/serviceName';
import { addDays, instant, ymd } from '../lib/time';
import { useDesktop } from '../lib/useDesktop';
import type { ChatMessage, InboxRow, Locale, Service } from '../types';
import { nextFiveMinutes } from './Booking';

const PILL: Record<string, string> = { whatsapp: 'wa', telegram: 'tg', instagram: 'ig', sms: 'sms' };
const SHORT: Record<string, string> = { whatsapp: 'WA', telegram: 'TG', instagram: 'IG', sms: 'SMS' };
const CHANNEL: Record<string, string> = { whatsapp: 'WhatsApp', telegram: 'Telegram', instagram: 'Instagram', sms: 'SMS' };
/** Drawn, faded and not tappable until the channel exists (owner, 2026-10-01). */
const LIVE = ['whatsapp', 'telegram'];
/** No filter chip at all; an SMS conversation still shows its channel (owner, 2026-10-04: not supported for now). */
const HIDDEN = ['sms'];
const threadKey = (clientId: string) => ['thread', clientId] as const;

/** «14:07» today, «вчера», else «24.09». */
function when(at: string, t: (k: string) => string): string {
  const today = ymd(new Date());
  if (ymd(at) === today) return formatTime(at);
  if (ymd(at) === addDays(today, -1)) return t('messages.yesterday');
  return ymd(at).slice(5).split('-').reverse().join('.');
}

export function InboxScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const [channel, setChannel] = useState('');
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState('');
  const inbox = useQuery({
    queryKey: inboxKey(channel, q.trim()),
    queryFn: () => api.inbox(channel || undefined, q.trim() || undefined),
    refetchInterval: INBOX_POLL_MS,
  });
  const rows = inbox.data?.items ?? [];
  const first = rows[0];

  return (
    <Shell active="messages">
      <AppBar
        title={t('nav.messages')}
        right={
          <button type="button" className="iconbtn" aria-label={t('messages.search')} onClick={() => setSearching((s) => !s)}>
            <Icon.srch />
          </button>
        }
      />
      <div style={{ flex: '0 0 auto', padding: '9px 16px 0', background: 'var(--surface)', borderBottom: '1px solid var(--line)' }}>
        {searching && (
          <label className="inp" style={{ borderRadius: 999, minHeight: 36, padding: '7px 13px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
            <Icon.srch />
            <input className="bare grow" type="search" autoFocus aria-label={t('messages.search')} placeholder={t('messages.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        )}
        <div className="chips" style={{ paddingBottom: 9 }}>
          <button type="button" className={`chip${channel === '' ? ' on' : ''}`} onClick={() => setChannel('')}>
            {t('messages.all')}
          </button>
          {Object.keys(CHANNEL).filter((c) => !HIDDEN.includes(c)).map((c) =>
            LIVE.includes(c) ? (
              <button key={c} type="button" className={`chip${channel === c ? ' on' : ''}`} onClick={() => setChannel(c)}>
                {CHANNEL[c]}
              </button>
            ) : (
              <span key={c} className="chip" aria-disabled="true" title={t('messages.soon')} style={{ opacity: 0.55 }}>
                {CHANNEL[c]}
              </span>
            ),
          )}
        </div>
      </div>
      <div className="content">
        <div className="pane" style={{ flex: desktop ? '0 0 auto' : '1 1 auto' }}>
          {inbox.isSuccess && rows.length === 0 ? (
            <p className="sm">{t('messages.empty')}</p>
          ) : (
            <ul className="list">
              {rows.map((r) => (
                <InboxItem key={r.client_id} row={r} on={r === first} onOpen={() => navigate(`/messages/${r.client_id}`)} />
              ))}
            </ul>
          )}
        </div>
        {/* Desktop only, also on a phone-width window: the class hides it there,
            so the flex layout lives on an inner box (owner, 2026-10-01). */}
        <div className="pane pane-2 desk-only" style={{ flex: '1 1 auto', padding: 0, borderLeft: '1px solid var(--line)' }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {first ? (
              <Conversation clientId={first.client_id} markRead={false} />
            ) : (
              <p className="sm" style={{ padding: 16 }}>
                {t('messages.pick')}
              </p>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

function InboxItem({ row, on, onOpen }: { row: InboxRow; on: boolean; onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <li className={on ? 'on' : ''} data-id={row.client_id} onClick={onOpen}>
      <span className="av">{row.name.charAt(0)}</span>
      <span className="grow" style={{ minWidth: 0 }}>
        <span className="row">
          <span className="nm grow">{row.name}</span>
          <span className="tnum">{when(row.last_at, t)}</span>
        </span>
        <span className="sm ell" style={{ display: 'block' }}>
          {row.last_body}
        </span>
      </span>
      <span className={`pill ${PILL[row.channel] ?? ''}`}>{SHORT[row.channel] ?? row.channel}</span>
    </li>
  );
}

/** The thread and its composer: screen 10's left pane, and 09's right pane on desktop. */
function Conversation({ clientId, markRead }: { clientId: string; markRead: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const thread = useQuery({ queryKey: threadKey(clientId), queryFn: () => api.thread(clientId), refetchInterval: INBOX_POLL_MS });
  const items = thread.data?.items ?? [];
  const unreadIn = items.some((m) => m.direction === 'in' && !m.read_at);

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [items.length]);

  // Opening the conversation reads it (doc 04 §8); allowed even while expired.
  useEffect(() => {
    if (!markRead || !unreadIn) return;
    void api.markRead(clientId).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      void queryClient.invalidateQueries({ queryKey: ['unread'] });
      void queryClient.invalidateQueries({ queryKey: threadKey(clientId) });
    });
  }, [markRead, unreadIn, clientId, queryClient]);

  const send = useMutation({
    mutationFn: (body: string) => api.reply(clientId, body),
    onSuccess: () => setText(''),
    onError: (e) => {
      // A refused message is kept as «Не отправлено» (the provider's own words on
      // hover); the text stays in the composer to retry.
      toast(errorText(e instanceof ApiError ? e.code : 'fallback'));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: threadKey(clientId) });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
      void queryClient.invalidateQueries({ queryKey: ['unread'] });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body = text.trim();
    if (body && !send.isPending) send.mutate(body);
  };

  return (
    <>
      <div className="thread" style={{ flex: '1 1 auto', overflow: 'auto' }}>
        {items.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
        <div ref={end} />
      </div>
      <form className="composer" onSubmit={submit}>
        <input className="inp" style={{ color: 'var(--ink)' }} aria-label={t('messages.placeholder')} placeholder={t('messages.placeholder')} value={text} onChange={(e) => setText(e.target.value)} />
        <button type="submit" className="iconbtn" aria-label={t('messages.send')} style={{ background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--accent-ink)' }}>
          <Icon.send />
        </button>
      </form>
    </>
  );
}

function Bubble({ m }: { m: ChatMessage }) {
  const { t } = useTranslation();
  const failed = m.status === 'failed';
  return (
    <div className={`msg ${m.direction}`} style={failed ? { border: '1px dashed var(--st-noshow-bd)' } : undefined} title={failed ? m.error ?? undefined : undefined}>
      <span style={{ whiteSpace: 'pre-wrap' }}>{m.body}</span>
      <span className="ts">
        {when(m.sent_at, t) === formatTime(m.sent_at) ? formatTime(m.sent_at) : `${when(m.sent_at, t)} ${formatTime(m.sent_at)}`}
        {failed && <b style={{ color: 'var(--st-noshow-fg)' }}> · {t('messages.failed')}</b>}
      </span>
    </div>
  );
}

export function ChatScreen() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;
  const navigate = useNavigate();
  const { clientId = '' } = useParams();
  const client = useQuery({ queryKey: ['client', clientId], queryFn: () => api.client(clientId) });
  const inbox = useQuery({ queryKey: inboxKey(), queryFn: () => api.inbox(), refetchInterval: INBOX_POLL_MS });
  const services = useQuery({ queryKey: ['services'], queryFn: api.services });
  const [service, setService] = useState<Service | null>(null);
  // Not read from the conversation yet (owner, 2026-10-01): the next free
  // five minutes, changed with the wheels on «Новая запись».
  const [start] = useState(() => ({ date: ymd(new Date()), time: nextFiveMinutes(new Date()) }));

  const channel = inbox.data?.items.find((r) => r.client_id === clientId)?.channel ?? client.data?.channels[0]?.channel;
  const card = client.data;
  const shown = `${start.date.split('-').reverse().join('.')} · ${start.time}`;
  const book = () => {
    const qs = new URLSearchParams({ client: clientId, start: instant(start.date, start.time) });
    if (service) qs.set('service', service.id);
    navigate(`/appointments/new?${qs}`);
  };

  return (
    <Shell active="messages">
      <AppBar
        back={() => navigate('/messages')}
        title={card?.name ?? ''}
        titleAfter={<ChannelLogo channel={channel} />}
        sub={card ? `${t('messages.clientCap')} · ${t('clients.visits', { count: card.visit_count }).toUpperCase()}` : undefined}
        right={
          <>
            {/* phone only: on a computer the right panel has its own «Записать» */}
            <button type="button" className="btn pri sm mob-only" onClick={book}>
              {t('messages.book')}
            </button>
            <button type="button" className="iconbtn" aria-label={t('messages.profile')} onClick={() => navigate(`/clients/${clientId}`)}>
              <Icon.ppl />
            </button>
          </>
        }
      />
      <div className="content">
        <div className="pane pane-wide" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
          <Conversation clientId={clientId} markRead />
        </div>
        <div className="pane pane-2">
          <span className="cap">{t('messages.bookFrom')}</span>
          <div className="fld">
            <label>{t('messages.service')}</label>
            <div className="chips">
              {services.data?.map((s) => (
                <button key={s.id} type="button" className={`chip${service?.id === s.id ? ' on' : ''}`} onClick={() => setService(s)}>
                  {serviceName(s.name_ru, s.name_ky, locale)}
                </button>
              ))}
            </div>
          </div>
          <div className="fld">
            <label>{t('messages.start')}</label>
            <div className="inp" role="button" tabIndex={0} onClick={book}>
              {shown}
            </div>
          </div>
          <div className="fld">
            <label>{t('messages.price')}</label>
            <div className="inp">{service ? `${formatAmount(service.price, locale)} ${t('money.som')}` : '—'}</div>
          </div>
          <button type="button" className="btn pri blk" onClick={book}>
            {t('messages.book')}
          </button>
        </div>
      </div>
    </Shell>
  );
}
